import PropTypes from "prop-types";
import { memo } from "react";

import { cn } from "@helpers/general";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";

import { useSubscriptionPopup } from "@context/SubscriptionPopupProvider";
import { useCurrentFormatCurrency } from "@hooks/usePrices";
import useApplyLuxeDiscountToProductPrice from "@hooks/useApplyLuxeDiscountToProductPrice";

import { ComponentType } from "@constants/ComponentType";
import TAG from "@constants/Tag";
import InfoIcon from "@svgs/info.svg";

function LuxePrice({ componentType, price, tags, tierDiscountPercent }) {
  const formatWithCurrency = useCurrentFormatCurrency();
  const { setSubscriptionPopupOpen } = useSubscriptionPopup();
  const applyLuxeDiscountToProductPrice = useApplyLuxeDiscountToProductPrice();

  const luxeFinalPrice = applyLuxeDiscountToProductPrice(price, tags, { tierDiscountPercent });
  const openSubscriptionPopup = () =>
    setSubscriptionPopupOpen(true, { isFinalSale: !!tags?.includes(TAG.FINAL_SALE) });

  return (
    <div data-class="showLuxePrices" className="text-secondary">
      <p
        className={cn("typo-body", {
          "flex flex-row-reverse gap-[5px]": componentType === ComponentType.FLOATING_ATC,
          "typo-h3 mobile:typo-h2 flex flex-row gap-[10px] mobile:flex-col mobile:items-end mobile:gap-0 mobile:mt-[3px]":
            componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS,
          "flex gap-[5px]":
            componentType === ComponentType.CP ||
            componentType === ComponentType.CP_ADS ||
            componentType === ComponentType.PRODUCT_CAROUSEL ||
            componentType === ComponentType.CP_OPTIONAL_PRODUCTS ||
            componentType === ComponentType.SEARCH,
        })}
        data-testid="luxe-price"
      >
        <span
          className={cn("font-bold mr-[6px] mobile:mr-0", {
            "flex flex-row items-center gap-[5px] typo-h3 mobile:typo-h2":
              componentType === ComponentType.PDP,
            "mr-0":
              componentType === ComponentType.PRODUCT_CAROUSEL ||
              componentType === ComponentType.CP ||
              componentType === ComponentType.CP_ADS ||
              componentType === ComponentType.SIDE_CART_UPSELL ||
              componentType === ComponentType.PDP ||
              componentType === ComponentType.PDP_ADS,
            "mobile:mr-[5px]": componentType === ComponentType.PRODUCT_RECOMMENDATIONS,
          })}
        >
          {componentType === ComponentType.PDP && (
            <button type="button" onClick={openSubscriptionPopup} className="hidden mobile:block">
              <InfoIcon width="15" height="15" />
            </button>
          )}
          {formatWithCurrency(luxeFinalPrice)}
        </span>
        <span
          className={cn({
            "typo-h3 mobile:typo-body":
              componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS,

            hidden: componentType === ComponentType.SIDE_CART_UPSELL,
          })}
        >
          Members
        </span>
        {componentType === ComponentType.PDP && (
          <button
            type="button"
            onClick={openSubscriptionPopup}
            className="hidden desktop:block ml-[-5px]"
          >
            <InfoIcon width="15" height="15" />
          </button>
        )}
      </p>
    </div>
  );
}

LuxePrice.propTypes = {
  componentType: PropTypes.string,
  price: PropTypes.number,
  tags: PropTypes.arrayOf(PropTypes.string),
  tierDiscountPercent: PropTypes.number,
};

export default withALErrorBoundary({
  name: "LuxePrice",
  priority: "P1",
})(memo(LuxePrice));
